import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Check, Loader2, Zap, Package, Crown, Star } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface PlanSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  availableCredits: number;
  onUseCredit: () => void;
  activeSubscription?: {
    plan_type: string;
    jobs_used: number;
    jobs_limit: number;
    highlights_used?: number;
    highlights_limit?: number;
  } | null;
}

const PLANS = [
  {
    id: "single",
    name: "Vaga Avulsa",
    price: "R$ 99",
    period: "",
    description: "1 vaga ativa por 30 dias",
    icon: Zap,
    features: [
      "1 publicação de vaga",
      "Ativa por 30 dias",
      "Visível para toda a comunidade",
    ],
  },
  {
    id: "core",
    name: "Core",
    price: "R$ 399",
    period: "/mês",
    description: "Até 5 vagas/mês + benefícios",
    icon: Package,
    badge: "Mais Popular",
    features: [
      "Até 5 vagas por mês",
      "1 vaga em destaque/mês",
      "Divulgação na comunidade",
      "Repost no LinkedIn",
    ],
  },
  {
    id: "plus",
    name: "Plus",
    price: "R$ 699",
    period: "/mês",
    description: "Até 10 vagas/mês + visibilidade máxima",
    icon: Crown,
    features: [
      "Até 10 vagas por mês",
      "2 vagas em destaque/mês",
      "Newsletter premium",
      "Banner rotativo",
      "Máxima visibilidade",
    ],
  },
];

export function PlanSelectionDialog({
  open,
  onOpenChange,
  jobId,
  availableCredits,
  onUseCredit,
  activeSubscription,
}: PlanSelectionDialogProps) {
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [isFeatured, setIsFeatured] = useState(false);
  const { toast } = useToast();

  const highlightsRemaining = activeSubscription
    ? (activeSubscription.highlights_limit || 0) - (activeSubscription.highlights_used || 0)
    : 0;

  const handleSelectPlan = async (planType: string) => {
    setIsLoading(planType);
    try {
      const { data, error } = await supabase.functions.invoke("create-job-checkout", {
        body: { plan_type: planType, job_id: jobId },
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error: any) {
      console.error("Error creating checkout:", error);
      toast({
        title: "Erro ao iniciar pagamento",
        description: error.message || "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(null);
    }
  };

  const handleUseSubscription = async () => {
    setIsLoading("subscription");
    try {
      const updateData: Record<string, any> = { status: "active" };
      if (isFeatured && highlightsRemaining > 0) {
        updateData.is_featured = true;
      }

      const { error: jobError } = await supabase
        .from("jobs")
        .update(updateData as any)
        .eq("id", jobId);

      if (jobError) throw jobError;

      // Increment jobs_used (and highlights if featured)
      const { data: sub } = await supabase
        .from("company_subscriptions")
        .select("id, jobs_used_this_period, highlights_used_this_period")
        .eq("status", "active")
        .maybeSingle();

      if (sub) {
        const subUpdate: Record<string, any> = {
          jobs_used_this_period: (sub.jobs_used_this_period || 0) + 1,
        };
        if (isFeatured && highlightsRemaining > 0) {
          subUpdate.highlights_used_this_period = (sub.highlights_used_this_period || 0) + 1;
        }
        await supabase
          .from("company_subscriptions")
          .update(subUpdate as any)
          .eq("id", sub.id);
      }

      toast({
        title: isFeatured ? "Vaga em destaque ativada! ⭐" : "Vaga ativada! 🎉",
        description: isFeatured
          ? "Sua vaga foi publicada em destaque usando seu plano ativo."
          : "Sua vaga foi publicada usando seu plano ativo.",
      });
      onUseCredit();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Erro ao ativar vaga",
        description: error.message || "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(null);
    }
  };

  const handleUseCredit = async () => {
    setIsLoading("credit");
    try {
      const { error: jobError } = await supabase
        .from("jobs")
        .update({ status: "active" })
        .eq("id", jobId);

      if (jobError) throw jobError;

      const { data: creditRecords } = await supabase
        .from("company_credits")
        .select("*")
        .gt("credits_remaining", 0)
        .order("created_at", { ascending: true })
        .limit(1);

      if (creditRecords && creditRecords.length > 0) {
        await supabase
          .from("company_credits")
          .update({ credits_remaining: creditRecords[0].credits_remaining - 1 })
          .eq("id", creditRecords[0].id);
      }

      toast({
        title: "Vaga ativada!",
        description: "Sua vaga foi publicada usando um crédito disponível.",
      });
      onUseCredit();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Erro ao usar crédito",
        description: error.message || "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(null);
    }
  };

  const hasActiveSubscription = activeSubscription && activeSubscription.jobs_used < activeSubscription.jobs_limit;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-xl">Escolha seu plano</DialogTitle>
          <DialogDescription>
            Sua vaga foi salva. Escolha um plano para publicá-la.
          </DialogDescription>
        </DialogHeader>

        {/* Active subscription banner */}
        {hasActiveSubscription && (
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-primary">
                  Plano {activeSubscription.plan_type === "plus" ? "Plus" : "Core"} ativo
                </p>
                <p className="text-sm text-muted-foreground">
                  {activeSubscription.jobs_limit - activeSubscription.jobs_used} vaga{activeSubscription.jobs_limit - activeSubscription.jobs_used !== 1 ? "s" : ""} disponível{activeSubscription.jobs_limit - activeSubscription.jobs_used !== 1 ? "is" : ""} · {highlightsRemaining} destaque{highlightsRemaining !== 1 ? "s" : ""} restante{highlightsRemaining !== 1 ? "s" : ""}
                </p>
              </div>
              <Button
                onClick={handleUseSubscription}
                disabled={isLoading !== null}
                className="bg-primary hover:bg-primary/90"
              >
                {isLoading === "subscription" ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Usar Vaga do Plano
              </Button>
            </div>
            {highlightsRemaining > 0 && (
              <div className="flex items-center gap-3 pt-2 border-t border-primary/10">
                <Switch
                  id="featured-switch"
                  checked={isFeatured}
                  onCheckedChange={setIsFeatured}
                />
                <Label htmlFor="featured-switch" className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                  Marcar como vaga em destaque ({highlightsRemaining} restante{highlightsRemaining !== 1 ? "s" : ""})
                </Label>
              </div>
            )}
          </div>
        )}

        {/* Legacy credits banner */}
        {availableCredits > 0 && !hasActiveSubscription && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-green-800">
                  Você tem {availableCredits} crédito{availableCredits > 1 ? "s" : ""} disponível{availableCredits > 1 ? "is" : ""}
                </p>
                <p className="text-sm text-green-600">Use um crédito para publicar esta vaga</p>
              </div>
              <Button
                onClick={handleUseCredit}
                disabled={isLoading !== null}
                className="bg-green-600 hover:bg-green-700"
              >
                {isLoading === "credit" ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Usar Crédito
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PLANS.map((plan) => {
            const isCurrentPlan = activeSubscription?.plan_type === plan.id;
            return (
              <div
                key={plan.id}
                className={`border rounded-lg p-5 flex flex-col relative hover:border-primary transition-colors ${
                  plan.id === "core" ? "border-primary shadow-md" : ""
                } ${isCurrentPlan ? "bg-primary/5" : ""}`}
              >
                {plan.badge && (
                  <Badge className="absolute -top-2 right-3 bg-primary text-primary-foreground text-xs">
                    {plan.badge}
                  </Badge>
                )}
                {isCurrentPlan && (
                  <Badge className="absolute -top-2 left-3 bg-green-600 text-white text-xs">
                    Seu Plano
                  </Badge>
                )}
                <div className="flex items-center gap-2 mb-2">
                  <plan.icon className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold">{plan.name}</h3>
                </div>
                <div className="mb-3">
                  <span className="text-2xl font-bold">{plan.price}</span>
                  {plan.period && (
                    <span className="text-sm text-muted-foreground">{plan.period}</span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>
                <ul className="space-y-2 mb-5 flex-1">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-500 shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={isLoading !== null || isCurrentPlan}
                  variant={plan.id === "core" ? "default" : "outline"}
                  className={plan.id === "core" ? "w-full bg-primary hover:bg-primary/90" : "w-full"}
                >
                  {isLoading === plan.id ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  {isCurrentPlan ? "Plano Atual" : plan.id === "single" ? "Comprar" : "Assinar"}
                </Button>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
